// Spirit Bolt look (owner 2026-11-02: reference sketch of a teardrop — round white-hot head, a long tapering tail, a yellow
// outer shell around a white inner shape — 'with the tail, but still fireball'). Each bolt is drawn as one flat card that
// lies along its flight direction and turns about that axis to face the camera (axial billboard). The shape is evaluated
// in the shader: head circle ∪ tapered tail, in three hard cel bands (white core → yellow body → thin orange rim), with
// scrolling flame noise that makes the rim and the tail tip ragged and flickering so it reads as a fireball, not a sticker.
// One InstancedMesh for every bolt; render-only.
import * as THREE from 'three/webgpu';
import { abs, color, float, instanceIndex, length, max, min, mix, mx_noise_float, step, uniform, uv, vec2, vec3 } from 'three/tsl';

const BODY = 0xffe21a; // Spirit Bolt yellow (owner: skills keep their own colour at every Surge level)

const MAX = 400;
/** card length / height (the head is one card-height across) */

/** shape options: aspect = card length / head height; tailW = tail half-width at the head (head units); vyK = how much of the
 *  vertical speed tilts the card (bolts fly flat-ish; Skyfall shards arc up and dive, so they follow it fully) */
export interface DropOpts { aspect?: number; tailW?: number; vyK?: number }

const _x = new THREE.Vector3(), _y = new THREE.Vector3(), _z = new THREE.Vector3(), _v = new THREE.Vector3();
const _m = new THREE.Matrix4();

export class BoltDrops {
  readonly mesh: THREE.InstancedMesh;
  private time = uniform(0);
  private n = 0;

  private aspect: number; private vyK: number;
  constructor(scene: THREE.Scene, o: DropOpts = {}) {
    const ASPECT = o.aspect ?? 2.3, tailW = o.tailW ?? 0.41; this.aspect = ASPECT; this.vyK = o.vyK ?? 0.35;
    const mat = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide, fog: false });
    const t = this.time, seed = float(instanceIndex).mul(7.31);
    // card space in head units: X 0 (tail tip) → ASPECT (nose), Y −0.5..0.5
    const X = uv().x.mul(ASPECT), Y = uv().y.sub(0.5);
    const cx = float(ASPECT - 0.5);                          // head centre
    const along = X.div(cx).clamp(0, 1);                     // 0 at the tail tip → 1 at the head
    // flame noise: scrolls back along the bolt, stronger toward the tail tip
    const n1 = mx_noise_float(vec3(X.mul(3.2).add(t.mul(15)), Y.mul(5), seed));
    const n2 = mx_noise_float(vec3(X.mul(7).add(t.mul(24)), Y.mul(9), seed.add(3.7)));
    const flick = n1.mul(0.7).add(n2.mul(0.3));
    const rag = float(0.035).add(float(1).sub(along).mul(0.11));  // edge raggedness (m in head units)
    // signed distance (negative inside) of the outer silhouette: head circle ∪ tapered tail
    const dHead = length(vec2(X.sub(cx), Y)).sub(0.5);
    const wTail = along.mul(tailW);
    const dTail = max(abs(Y).sub(wTail), X.sub(cx));          // only up to the head centre
    const dOut = min(dHead, dTail).add(flick.mul(rag));
    // inner (white) shape: a smaller head pushed toward the nose + a thinner tail starting further back
    const ci = cx.add(0.13);
    const dHeadIn = length(vec2(X.sub(ci), Y)).sub(0.33);
    const alongIn = X.sub(0.42).div(ci.sub(0.42)).clamp(0, 1);
    const dTailIn = max(max(abs(Y).sub(alongIn.mul(tailW * 0.56)), X.sub(ci)), float(0.42).sub(X));
    const dIn = min(dHeadIn, dTailIn).add(flick.mul(rag.mul(0.6)));
    const inside = step(dOut, 0);
    const core = step(dIn, 0);
    const rim = step(float(-0.055).sub(float(1).sub(along).mul(0.03)), dOut); // thin outer band (thicker toward the tail)
    const body = color(BODY);
    let col = mix(body, color(0xffffff), core);
    col = mix(col, color(0xffb21e), rim.mul(float(1).sub(core))); // owner: less orange, more yellow-ish orange
    mat.colorNode = col.mul(1.35);                            // a little over 1: a touch of bloom on the white head
    mat.opacityNode = inside.mul(float(0.82).add(along.mul(0.18)));
    this.mesh = new THREE.InstancedMesh(new THREE.PlaneGeometry(1, 1), mat, MAX);
    this.mesh.frustumCulled = false; this.mesh.count = 0; this.mesh.renderOrder = 4;
    scene.add(this.mesh);
  }

  begin(dt: number): void { this.time.value += dt; this.n = 0; }

  /** a bolt at (x,y,z) flying along (vx,vy,vz); size = head diameter (m) */
  push(x: number, y: number, z: number, vx: number, vy: number, vz: number, size: number, cam: THREE.Vector3): void {
    if (this.n >= MAX) return;
    const ASPECT = this.aspect;
    _x.set(vx, vy * this.vyK, vz); if (_x.lengthSq() < 1e-6) _x.set(1, 0, 0); _x.normalize();
    _v.set(cam.x - x, cam.y - y, cam.z - z).normalize();
    _y.crossVectors(_v, _x); if (_y.lengthSq() < 1e-6) _y.set(0, 1, 0); _y.normalize();
    _z.crossVectors(_x, _y);
    const L = size * ASPECT, H = size;
    _m.makeBasis(_x.multiplyScalar(L), _y.multiplyScalar(H), _z);
    // the card centre sits behind the bolt so the head circle is centred on the projectile
    const back = (0.5 - (ASPECT - 0.5) / ASPECT) * L; // centre → head centre is +(…)·L, so the centre sits behind the bolt
    _x.normalize();
    _m.setPosition(x + _x.x * back, y + _x.y * back, z + _x.z * back);
    this.mesh.setMatrixAt(this.n, _m);
    this.n++;
  }

  end(): void {
    this.mesh.count = this.n;
    this.mesh.instanceMatrix.needsUpdate = true;
  }
}

